import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormControl, MenuItem, Select, Stack } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z, type ZodSchema } from "zod";

interface SelectOption {
  label: string;
  value: string;
}

interface SaveableSelectProps {
  label: string;
  value: string;
  options: SelectOption[];
  schema: ZodSchema<string>;
  onSave: (value: string) => void;
  disabled?: boolean;
}

interface FormValues {
  value: string;
}

export function SaveableSelect(props: SaveableSelectProps) {
  const { label, value, options, schema, onSave, disabled = false } = props;

  const formSchema = useMemo(() => z.object({ value: schema }), [schema]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    trigger,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { value },
    mode: "onChange",
  });

  useEffect(() => {
    reset({ value });
  }, [value, reset]);

  // Validate on mount so errors (e.g. "Required") show immediately,
  // since mode: "onChange" only validates after user interaction.
  useEffect(() => {
    void trigger("value");
  }, [trigger]);

  function onSubmit(data: FormValues) {
    onSave(data.value);
    reset({ value: data.value });
  }

  const hasError = isDefined(errors.value);

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Text variant="body1" sx={{ minWidth: 130 }}>
        {label}
      </Text>
      <FormControl size="small" sx={{ width: 280 }}>
        <Controller
          name="value"
          control={control}
          render={({ field }) => (
            <Select {...field} disabled={disabled || isSubmitting}>
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </FormControl>
      <Button
        variant="text"
        size="small"
        disabled={disabled || isSubmitting || hasError || !isDirty}
        onClick={handleSubmit(onSubmit)}
      >
        {isSubmitting ? "Saving..." : "Save"}
      </Button>
      {hasError && (
        <Text variant="body2" color="warning.main">
          {errors.value?.message}
        </Text>
      )}
    </Stack>
  );
}
